import logging from '@sstdev/lib_logging';
import notificationTypes from '../constants/notificationTypes';

// What zebraRfidService shows the user while native code reconnects an RFID reader that stopped
// answering, and when native code gives up: the reconnect progress dialog, the "not responding"
// and "in use" messages, and the prompt to restart the app. `state` is zebraRfidService's private
// state, so the open dialog, the open prompt and the time the last message was shown are kept in
// one place. `getDispatch` returns zebraRfidService's current dispatch, which init sets later.
export default function createReaderStatusHandlers(state, getDispatch) {
    const dispatch = (...args) => getDispatch()(...args);

    // Coming back to the app also opens the sync progress dialog, which hides toasts until it
    // closes (about 8 s on a TC22R), so this toast stays up long enough to read after that.
    const READER_NOT_RESPONDING_TOAST_MS = 20000;

    // Native code sends this when the reader, which the SDK still reports as connected, does not
    // answer a command: a trigger pull, a power change, or the check run when the app comes back to
    // the foreground. In testing that happened after another app had connected to the reader, and
    // also with no other app running. Native code sends it only after restarting the reader's
    // process did not bring the reader back (see handleReaderReconnecting), so the user is asked to
    // close and reopen the app.
    function handleReaderNotResponding() {
        logging.info('[RFID] The reader did not answer a command.');
        tellUserReaderFailed({
            toast: 'The RFID reader stopped responding. Close any other RFID app (for example 123RFID), then close this app and reopen it.',
            promptTitle: 'RFID Reader Not Responding',
            promptMessage:
                'The RFID reader stopped responding. Restart this app now? Anything not saved on this screen will be lost.'
        });
    }

    // Native code sends this instead of RfidReaderNotResponding when it stopped reconnecting and
    // the last try failed because another app, such as 123RFID Mobile, is connected to the reader.
    // The reader can't connect to this app until that app is closed.
    function handleReaderInUse() {
        logging.info('[RFID] Another app is using the reader.');
        tellUserReaderFailed({
            toast: 'Another RFID app, such as 123RFID, is using the RFID reader. Close that app, then close this app and reopen it.',
            promptTitle: 'RFID Reader In Use',
            promptMessage:
                'Another RFID app, such as 123RFID, is using the RFID reader. Close that app, then tap Restart. Anything not saved on this screen will be lost.'
        });
    }

    // Native code sends this on an MC33 when connecting failed because another app, such as
    // 123RFID Mobile, is connected to the reader. Zebra's RFID service won't give the reader to this
    // app until that app lets go of it. Native code connects again when this app comes back to the
    // foreground, so the user doesn't need to restart this app and gets no Restart prompt.
    function handleReaderBusy() {
        logging.info('[RFID] Could not connect: another app is using the reader.');
        tellUserReaderFailed({
            toast: 'Another RFID app, such as 123RFID, is using the RFID reader. Close that app (swipe it away in the recent apps list), then come back to this app to scan.'
        });
    }

    // Without a promptTitle, only shows the toast.
    function tellUserReaderFailed({ toast, promptTitle, promptMessage }) {
        closeReconnectProgress();
        // Once the reader stops answering, later commands time out too. Skip the repeats while the
        // first toast is still on screen; the notification list already has the full text.
        const now = Date.now();
        if (now - state.readerNotRespondingShownAt < READER_NOT_RESPONDING_TOAST_MS) return;
        state.readerNotRespondingShownAt = now;
        dispatch(
            {
                isError: true, //todo: gl2025 remove
                variant: 'error',
                type: notificationTypes.TOAST,
                message: toast,
                timeout: READER_NOT_RESPONDING_TOAST_MS,
                addToList: true
            },
            { verb: 'pop', namespace: 'application', relation: 'notification' }
        );
        if (promptTitle) offerRestart(promptTitle, promptMessage);
    }

    // Only the app's process dying brought the reader back in testing, so offer to restart the app.
    // The toast above stays as the fallback if the user picks Not now, or if the native build has
    // no restartApp. Only one prompt is open at a time, so unanswered prompts don't pile up.
    function offerRestart(title, message) {
        const restartApp = state.NativeModules.RfidReader?.restartApp;
        if (typeof restartApp !== 'function' || state.restartPromptOpen) return;
        state.restartPromptOpen = true;
        dispatch(
            {
                title,
                message,
                okButtonText: 'Restart',
                cancelButtonText: 'Not now',
                variant: 'warning',
                okAction: () => {
                    state.restartPromptOpen = false;
                    logging.info('[RFID] The user chose to restart the app.');
                    restartApp();
                },
                cancelAction: () => {
                    state.restartPromptOpen = false;
                    logging.info('[RFID] The user chose not to restart the app.');
                }
            },
            { verb: 'confirm', namespace: 'application', relation: 'user' }
        );
    }

    const RECONNECT_TITLE = 'Reconnecting the RFID reader';
    const RECONNECT_BAR_TITLE = 'Retrying';
    // Reconnecting took 9.4 to 9.6 s in testing on a Zebra TC22R. The bar fills over this time and
    // then waits at the last step until the reader answers.
    const RECONNECT_EXPECTED_SECONDS = 10;
    // Closes the dialog if neither "reconnected" nor "not responding" (or "in use") arrives, so it
    // can't stay open.
    const RECONNECT_PROGRESS_MAX_MS = 60000;
    const READER_RECONNECTED_TOAST_MS = 3000;

    // Native code sends this when the reader did not answer a command and the app is restarting the
    // reader's process to bring it back (RfidReaderClient). Scans can't work until that is done, so
    // a progress dialog stays open until native code sends RfidReaderReconnected, or
    // RfidReaderNotResponding if reconnecting failed. Native code sends this again when a try fails
    // and it tries again; the dialog then says so and its bar starts over.
    function handleReaderReconnecting() {
        logging.info('[RFID] Reconnecting the reader.');
        const isRetry = state.reconnectProgressTimer != null;
        clearReconnectTimers();
        const description = isRetry
            ? "That try didn't work. Trying again. If another RFID app such as 123RFID is open, close it."
            : 'The RFID reader stopped responding. Reconnecting it takes about 10 seconds.';
        let seconds = 0;
        const showProgress = () =>
            dispatch(
                {
                    mainTitle: RECONNECT_TITLE,
                    description,
                    title: RECONNECT_BAR_TITLE,
                    current: seconds,
                    total: RECONNECT_EXPECTED_SECONDS
                },
                { verb: 'update', namespace: 'application', relation: 'progress' }
            );
        showProgress();
        state.reconnectProgressTimer = setInterval(() => {
            if (seconds >= RECONNECT_EXPECTED_SECONDS - 1) return;
            seconds++;
            showProgress();
        }, 1000);
        state.reconnectProgressCloseTimer = setTimeout(closeReconnectProgress, RECONNECT_PROGRESS_MAX_MS);
    }

    function handleReaderReconnected() {
        logging.info('[RFID] The reader reconnected.');
        closeReconnectProgress();
        dispatch(
            {
                variant: 'success',
                type: notificationTypes.TOAST,
                message: 'RFID reader reconnected',
                timeout: READER_RECONNECTED_TOAST_MS
            },
            { verb: 'pop', namespace: 'application', relation: 'notification' }
        );
    }

    // Native code sends RfidReaderConnected when the reader is ready after the device was put into
    // its sled. Connecting takes a few seconds after that, and a trigger pull before then reads
    // nothing, so this tells the user when they can scan. On an MC33 it is also sent when a
    // connect works after another app had the reader (see handleReaderBusy).
    function handleReaderConnected() {
        logging.info('[RFID] The reader connected.');
        closeReconnectProgress();
        dispatch(
            {
                variant: 'success',
                type: notificationTypes.TOAST,
                message: 'RFID reader connected',
                timeout: READER_RECONNECTED_TOAST_MS
            },
            { verb: 'pop', namespace: 'application', relation: 'notification' }
        );
    }

    function closeReconnectProgress() {
        if (state.reconnectProgressTimer == null) return;
        clearReconnectTimers();
        dispatch({ mainTitle: RECONNECT_TITLE }, { verb: 'reset', namespace: 'application', relation: 'progress' });
    }

    function clearReconnectTimers() {
        clearInterval(state.reconnectProgressTimer);
        clearTimeout(state.reconnectProgressCloseTimer);
        state.reconnectProgressTimer = null;
        state.reconnectProgressCloseTimer = null;
    }

    return {
        handleReaderNotResponding,
        handleReaderInUse,
        handleReaderBusy,
        handleReaderReconnecting,
        handleReaderReconnected,
        handleReaderConnected,
        closeReconnectProgress
    };
}
